import React, { ChangeEvent, FormEvent } from "react";
import ReactModal from "react-modal";
import { Link } from "react-router-dom";

import { V2, apiGet } from "../../Api";
import { formatDateTimeLocal, formatName, formatSize, formatMimeLong } from "../../Utilities";
import { IS_PRE_RELEASE } from "../../version";
import withTitle from "../withTitle";
import DelayInput from "../DelayInput";
import FileUploadDialog from "../FileUploadDialog";
import ViewToolbar from "../ViewToolbar";
import ToolbarMenuItem from "../ToolbarMenuItem";

import "../viewer.css";


interface IFilesAdminViewState {
    isLoading: boolean | string;
    isDeleting: boolean | string;
    isFileDialogOpen: boolean;
    isFileDeletionOpen: boolean;

    files: any[];
    deletionInfo: any;

    people: any[];
    searchText: string;
    searchPerson: string;

    blobs: string[];

    [name: string]: any;
}

class FilesAdminView extends React.Component<{}, IFilesAdminViewState> {
    constructor(props: Readonly<{}>) {
        super(props);

        const state: IFilesAdminViewState = {
            isLoading: true,
            isFileDialogOpen: false,
            isFileDeletionOpen: false,
            isDeleting: false,

            files: [],
            deletionInfo: {},
            people: [],

            searchText: "",
            searchPerson: "",

            blobs: []
        };

        this.state = state;

        this.handleElementChanged = this.handleElementChanged.bind(this);
        this.toggleUploadDialog = this.toggleUploadDialog.bind(this);

        this.closeDeletionDialog = this.closeDeletionDialog.bind(this);
        this.realDeleteFile = this.realDeleteFile.bind(this);
        this.openLink = this.openLink.bind(this);
    }

    public async componentDidMount() {
        try {
            const people = await apiGet<any[]>("employees/summary?limit=400");

            this.setState({
                people: people.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)))
            });

            this.loadFiles();
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load employee list"
            });
        }
    }

    public componentWillUnmount() {
        this.state.blobs.forEach(a => URL.revokeObjectURL(a));
    }

    public render() {
        return (
            <div className="viewer-root">
                <ViewToolbar>
                    {IS_PRE_RELEASE ? <ToolbarMenuItem><button type="button" title="Test" className="icon-cogs" onClick={() => this.recompressImages()} /></ToolbarMenuItem> : null}
                    <ToolbarMenuItem><button type="button" title="Upload file" className="icon-plus" onClick={this.toggleUploadDialog} /></ToolbarMenuItem>
                </ViewToolbar>
                <FileUploadDialog isOpen={this.state.isFileDialogOpen} allowMultiple={true} allowCompression={true} onRequestClose={this.toggleUploadDialog} />
                <ReactModal
                    isOpen={this.state.isFileDeletionOpen} onRequestClose={this.closeDeletionDialog}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-deletion-portal"
                    bodyOpenClassName="modal-deletion-open"
                    contentLabel="Confirm file deletion">
                    <form className="input-form" onSubmit={this.realDeleteFile}>
                        <div className="info-block">
                            <header>
                                <h3>File Upload</h3>
                            </header>

                            <p>
                                Are you sure you want to delete this file?<br />
                                <code>{this.state.deletionInfo.id}</code><br />
                                {this.state.deletionInfo.title || "No description"}<br />
                                Uploaded by {this.state.deletionInfo.employee && formatName(this.state.deletionInfo.employee.name)}<br />
                            </p>
                            &nbsp;
                            {this.state.isDeleting === true
                                ? <div className="spinner-outer-text"><span className="spinner lds-dual-ring" />&nbsp;Deleting...</div>
                                : typeof this.state.isDeleting === "string"
                                    ? <span className="error-text">{this.state.isDeleting}</span>
                                    : undefined
                            }
                            <div className="action-panel-dialog">
                                <button type="submit" className="button red push-right text-with-icon" disabled={this.state.isDeleting === true}>Delete File</button>
                                <button type="button" className="button" onClick={this.closeDeletionDialog}>Close</button>
                            </div>
                        </div>
                    </form>
                </ReactModal>
                <div className="filter-control-box">
                    <DelayInput className="fill-space" type="search" timeout={500} name="searchText" value={this.state.searchText} placeholder="Search..." onChangeDelayed={this.handleElementChanged} />
                    <select name="searchPerson" value={this.state.searchPerson} onChange={this.handleElementChanged}>
                        <option value="">Everyone</option>
                        {this.state.people.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                    </select>
                </div>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-smallest">File ID</th>
                            <th className="col-smallest">Date Uploaded</th>
                            <th className="col-smallest">Uploaded by</th>
                            <th className="col-smallest">Size</th>
                            <th className="col-smallest">Type</th>
                            <th>Description</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Files</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            this.state.isLoading === true
                                ? <tr><td colSpan={10}><div className="spinner spinner-center lds-dual-ring" /></td></tr>
                                : typeof this.state.isLoading === "string"
                                    ? <tr><td colSpan={10} className="error-text">{this.state.isLoading}</td></tr>
                                    : this.state.files.length === 0
                                        ? <tr><td colSpan={10}>No files found</td></tr>
                                        : this.state.files.map(a => (
                                            <tr key={a.id}>
                                                <td data-caption="File ID: "><Link to={`/files/${a.id}`}><code>{a.id}</code></Link></td>
                                                <td data-caption="Date Uploaded: ">{formatDateTimeLocal(a.creationTimestamp)}</td>
                                                <td data-caption="Uploaded by: ">{formatName(a.employee.name)}</td>
                                                <td data-caption="Size: " className="col-right-vt">{formatSize(a.contentLength)}</td>
                                                <td data-caption="Type: "><code>{formatMimeLong(a.mime)}</code></td>
                                                <td data-caption="Description: " className="wrap-full">{a.title}</td>
                                                <td className="col-nopad">
                                                    <button type="button" className="button btn-light red small" title="Delete file" onClick={() => this.deleteFile(a)}><i className="icon-trash" /></button>
                                                </td>
                                            </tr>
                                        ))
                        }
                    </tbody>
                </table>
            </div>
        );
    }

    private async loadFiles() {
        try {
            this.setState({
                isLoading: true
            });

            const parameters = [];
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchPerson) {
                parameters.push(`person=${this.state.searchPerson}`);
            }

            const files = await V2.get<any[]>(`files?${parameters.join("&")}`);

            this.setState({
                isLoading: false,

                files
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load file list"
            });
        }
    }

    private handleElementChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.setState({
            [name]: value
        }, () => this.loadFiles());
    }

    private toggleUploadDialog() {
        this.setState(s => {
            if (s.isFileDialogOpen) {
                this.loadFiles();
            }

            return { isFileDialogOpen: !s.isFileDialogOpen };
        }, () => !this.state.isFileDialogOpen && this.loadFiles());
    }

    private closeDeletionDialog() {
        this.setState({
            isFileDeletionOpen: false,
        });
    }

    private async deleteFile(file: any) {
        this.setState({
            isFileDeletionOpen: true,
            deletionInfo: file,
            isDeleting: false
        });
    }

    private async realDeleteFile(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                isDeleting: true,
            });

            await V2.delete(`files/${this.state.deletionInfo.id}`);

            this.setState({
                isFileDeletionOpen: false,
                isDeleting: false,
            }, () => this.loadFiles());
        }
        catch (e) {
            this.setState({
                isDeleting: e.message || e || "Could not delete file"
            });
        }
    }

    private openLink(id: string) {
        // Load image into blob
        V2.get<Blob>(`files/${id}`)
            .then(v => {
                const blob = URL.createObjectURL(v);
                const wnd = window.open(blob, "file_window", "resizable,scrollbars,status");

                if (wnd) {
                    wnd.addEventListener("unload", e => {
                        if ((e.currentTarget! as Window).location.protocol === "blob:") {
                            URL.revokeObjectURL(blob);
                        }
                    });
                }
            });
    }

    private async recompressImages() {
        await V2.post("files/recompress");
    }
}

export default withTitle("Uploaded Files", FilesAdminView);